/**
 * SitePicker — searchable dropdown of sites, backed by the sites stores.
 *
 * Shared by the AI pages that operate on one site at a time (/diagnose,
 * /closeout, /watch). The estate is 1000+ sites, so the rendered list is
 * capped and a search box narrows it.
 *
 * Vendor-aware: pass `vendor` + `onVendorChange` to surface a vendor
 * toggle and let the picker draw from any vendor's site list. The
 * `vendors` prop controls which tabs show (default Meraki + Aruba; the
 * /diagnose page also offers CLI). Without the vendor props the picker
 * is Meraki-only. Every picked site carries its `vendor` so callers
 * route the request correctly.
 */
import { useMemo, useState } from "react";
import { Building2, ChevronDown, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/cn";
import { arubaSlug } from "@/lib/aruba-slug";
import { useSites } from "@/stores/sites";
import { useArubaSites } from "@/stores/aruba-sites";
import { useCliSites } from "@/stores/cli-sites";

export type SiteVendor = "meraki" | "aruba" | "cli";

/** Vendor-neutral picked-site shape — the slug + display fields every
 *  consumer needs, plus the vendor so the caller routes the request. */
export interface PickedSite {
  slug: string;
  code: string;
  org: string;
  vendor: SiteVendor;
}

const VENDOR_LABEL: Record<SiteVendor, string> = {
  meraki: "Meraki",
  aruba: "Aruba",
  cli: "CLI",
};

export interface SitePickerProps {
  value: PickedSite | null;
  onPick: (s: PickedSite) => void;
  /** When provided (with onVendorChange), a vendor toggle shows. */
  vendor?: SiteVendor;
  onVendorChange?: (v: SiteVendor) => void;
  /** Which vendor tabs to offer. Default Meraki + Aruba. */
  vendors?: SiteVendor[];
  /** Trigger label shown when nothing is selected. */
  placeholder?: string;
}

export function SitePicker({
  value,
  onPick,
  vendor,
  onVendorChange,
  vendors = ["meraki", "aruba"],
  placeholder = "Select a site…",
}: SitePickerProps) {
  const merakiSites = useSites((s) => s.sites);
  const merakiStatus = useSites((s) => s.status);
  const loadMeraki = useSites((s) => s.loadSites);

  const arubaSites = useArubaSites((s) => s.data);
  const arubaStatus = useArubaSites((s) => s.status);
  const loadAruba = useArubaSites((s) => s.load);

  const cliSites = useCliSites((s) => s.data);
  const cliStatus = useCliSites((s) => s.status);
  const loadCli = useCliSites((s) => s.load);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const showToggle = vendor !== undefined && onVendorChange !== undefined;
  const activeVendor: SiteVendor = vendor ?? "meraki";


  // Normalize whichever vendor's list is active into PickedSite rows.
  const allOptions: PickedSite[] = useMemo(() => {
    if (activeVendor === "aruba") {
      return arubaSites.map((s) => ({
        slug: arubaSlug(s.site_name ?? ""),
        code: s.site_name ?? "(unnamed site)",
        org: [s.city, s.country].filter(Boolean).join(", ") || "Aruba Central",
        vendor: "aruba" as const,
      }));
    }
    if (activeVendor === "cli") {
      return cliSites.map((g) => ({
        slug: arubaSlug(g.name),
        code: g.name,
        org: `${g.hostCount} host${g.hostCount === 1 ? "" : "s"} · Zabbix`,
        vendor: "cli" as const,
      }));
    }
    return merakiSites.map((s) => ({
      slug: s.slug,
      code: s.code,
      org: s.org,
      vendor: "meraki" as const,
    }));
  }, [activeVendor, merakiSites, arubaSites, cliSites]);

  const loading =
    activeVendor === "aruba"
      ? arubaStatus === "loading" && arubaSites.length === 0
      : activeVendor === "cli"
        ? cliStatus === "loading" && cliSites.length === 0
        : merakiStatus === "loading" && merakiSites.length === 0;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matched = q
      ? allOptions.filter(
          (s) =>
            s.code.toLowerCase().includes(q) ||
            s.slug.toLowerCase().includes(q) ||
            s.org.toLowerCase().includes(q),
        )
      : allOptions;
    // Cap the rendered list — the estate is 1000+ sites.
    return matched.slice(0, 50);
  }, [allOptions, query]);

  return (
    <DropdownMenu open={open} onOpenChange={(next) => {
        setOpen(next);
        if (next) {
          if (activeVendor === "meraki" && merakiStatus === "idle") void loadMeraki();
          if (activeVendor === "aruba" && arubaStatus === "idle") void loadAruba();
          if (activeVendor === "cli" && cliStatus === "idle") void loadCli();
        }
      }}>
      <DropdownMenuTrigger className="flex h-10 w-full items-center justify-between gap-3 rounded-xl border border-line bg-surface px-3.5 text-sm text-fg hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet/50">
        <span className="flex min-w-0 items-center gap-2.5">
          <Building2 size={14} className="shrink-0 text-fg-mute" />
          {value ? (
            <span className="flex min-w-0 flex-col items-start text-left leading-tight">
              <span className="truncate font-semibold text-fg">{value.code}</span>
              <span className="truncate text-[10.5px] text-fg-mute">
                {value.vendor !== "meraki" ? `${VENDOR_LABEL[value.vendor]} · ` : ""}
                {value.org}
              </span>
            </span>
          ) : (
            <span className="text-fg-mute">{placeholder}</span>
          )}
        </span>
        <ChevronDown size={14} className="shrink-0 text-fg-mute" />
      </DropdownMenuTrigger>

      <DropdownMenuContent align="start" className="w-[460px] p-0">
        {showToggle ? (
          <div className="flex gap-1 border-b border-line p-2">
            {vendors.map((v) => (
              <button
                key={v}
                type="button"
                onClick={() => onVendorChange?.(v)}
                className={cn(
                  "flex-1 rounded-md px-3 py-1.5 text-xs font-semibold",
                  activeVendor === v
                    ? "bg-aurora-soft text-violet"
                    : "text-fg-mute hover:bg-surface-2",
                )}
              >
                {VENDOR_LABEL[v]}
              </button>
            ))}
          </div>
        ) : null}
        <div className="border-b border-line p-2">
          <div className="relative">
            <Search
              size={13}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-mute"
            />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Filter by site code, slug, or org…"
              className="h-9 pl-8 text-xs"
              autoFocus
            />
          </div>
        </div>
        <div className="max-h-[340px] overflow-y-auto py-1">
          {loading ? (
            <div className="px-3 py-6 text-center text-xs text-fg-mute">Loading sites…</div>
          ) : visible.length === 0 ? (
            <div className="px-3 py-6 text-center text-xs text-fg-mute">
              {query ? `No sites match "${query}"` : "No sites available"}
            </div>
          ) : (
            visible.map((s) => (
              <button
                key={`${s.vendor}:${s.slug}`}
                type="button"
                onClick={() => {
                  onPick(s);
                  setOpen(false);
                  setQuery("");
                }}
                className="flex w-full items-start gap-2.5 rounded-md px-3 py-2 text-left hover:bg-aurora-soft"
              >
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-fg">{s.code}</div>
                  <div className="mt-0.5 truncate text-[11px] text-fg-mute">{s.org}</div>
                </div>
                <span className="shrink-0 font-mono text-[10px] text-fg-mute">{s.slug}</span>
              </button>
            ))
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
